import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Collapse,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CheckCircle as ValidateIcon,
  Cancel as RefuseIcon,
  Block as BlockIcon,
  LocalShipping as DeliveryIcon,
  Inventory as ReceivedIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  DemandeAchat,
  DemandeAchatStatus,
  DEMANDE_ACHAT_STATUS_LABELS,
} from '../../../types/demandeAchat';
import { demandesAchatAPI, DemandeAchatFilters } from '../../../services/api';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { extractErrorMessage } from '../../../utils/errorHandler';
import PermissionButton from '../../../components/PermissionButton';
import CustomTablePagination from '../../../components/CustomTablePagination';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import DemandeAchatFormDialog from '../DemandeAchatFormDialog';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useDemandeAchatsList,
  useDeletedDemandeAchats,
  useInvalidateDemandeAchats,
} from '../../../hooks/queries/useDemandeAchats';
import { useProjetsDropdown, useUsersDropdown } from '../../../hooks/queries/useDropdowns';

const STOCK_COLOR = '#2196F3';
const MANAGER_ROLES = ['admin', 'Manager', 'Developer'];

const DA_FILTER_SCHEMA = {
  date:        { type: 'dateRange',         paramFrom: 'start_date', paramTo: 'end_date' },
  reference:   { type: 'text',              param: 'reference' },
  designation: { type: 'text',              param: 'designation' },
  user_ids:    { type: 'multiAutocomplete', param: 'user_ids' },
  projet_ids:  { type: 'multiAutocomplete', param: 'projet_ids' },
  statuses:    { type: 'multiSelect',       param: 'statuses' },
} as const;

const STATUS_OPTIONS = [
  { value: DemandeAchatStatus.EN_COURS_TRAITEMENT, label: 'En cours de traitement' },
  { value: DemandeAchatStatus.VALIDE, label: 'Validé' },
  { value: DemandeAchatStatus.REFUSE, label: 'Refusé' },
  { value: DemandeAchatStatus.BLOQUE, label: 'Bloqué' },
  { value: DemandeAchatStatus.EN_COURS_LIVRAISON, label: 'En cours de livraison' },
  { value: DemandeAchatStatus.RECEPTIONNE, label: 'Réceptionné' },
];

const getStatusChip = (status: string) => {
  switch (status) {
    case DemandeAchatStatus.EN_COURS_TRAITEMENT:
      return <Chip label="En cours de traitement" color="warning" size="small" />;
    case DemandeAchatStatus.VALIDE:
      return <Chip label="Validé" color="success" size="small" />;
    case DemandeAchatStatus.REFUSE:
      return <Chip label="Refusé" color="error" size="small" />;
    case DemandeAchatStatus.BLOQUE:
      return (
        <Chip
          label="Bloqué"
          size="small"
          sx={{ bgcolor: 'grey.600', color: 'white' }}
        />
      );
    case DemandeAchatStatus.EN_COURS_LIVRAISON:
      return <Chip label="En cours de livraison" color="info" size="small" />;
    case DemandeAchatStatus.RECEPTIONNE:
      return <Chip label="Réceptionné" color="primary" size="small" />;
    default:
      return <Chip label={DEMANDE_ACHAT_STATUS_LABELS[status] || status} size="small" />;
  }
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('fr-FR');
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

interface TimelineEntry {
  label: string;
  at?: string | null;
  by?: string | null;
  color: string;
}

const buildTimeline = (da: DemandeAchat): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    { label: 'Créée', at: da.created_at, by: da.user_name, color: 'warning.main' },
  ];
  if (da.valide_at) entries.push({ label: 'Validée', at: da.valide_at, by: da.valide_user_name, color: 'success.main' });
  if (da.refuse_at) entries.push({ label: 'Refusée', at: da.refuse_at, by: da.refuse_user_name, color: 'error.main' });
  if (da.bloque_at) entries.push({ label: 'Bloquée', at: da.bloque_at, by: da.bloque_user_name, color: 'grey.700' });
  if (da.livraison_at) entries.push({ label: 'En cours de livraison', at: da.livraison_at, by: da.livraison_user_name, color: 'info.main' });
  if (da.receptionne_at) entries.push({ label: 'Réceptionnée', at: da.receptionne_at, by: da.receptionne_user_name, color: 'primary.main' });
  // Sort by date
  return entries.sort((a, b) => {
    if (!a.at) return -1;
    if (!b.at) return 1;
    return new Date(a.at).getTime() - new Date(b.at).getTime();
  });
};

const DemandesAchatManagement: React.FC = () => {
  const { user } = useAuth();
  const isManager = useMemo(
    () => MANAGER_ROLES.includes(user?.role || '') || MANAGER_ROLES.includes(user?.user_role?.name || ''),
    [user],
  );

  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    DA_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const { data: projets = [] } = useProjetsDropdown();
  const { data: users = [] } = useUsersDropdown(isManager); // cached; only for managers
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>(undefined);

  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogTarget, setStatusDialogTarget] = useState<{ id: number; reference: string; newStatus: string; label: string } | null>(null);
  const [statusComment, setStatusComment] = useState('');

  // Status menu anchor
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTargetDA, setMenuTargetDA] = useState<DemandeAchat | null>(null);

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const listParams = useMemo(() => {
    const filterBag = toQueryParams() as DemandeAchatFilters;
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      filters: Object.keys(filterBag).length > 0 ? filterBag : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const listQuery = useDemandeAchatsList(listParams, tabValue === 0);
  const deletedQuery = useDeletedDemandeAchats(tabValue === 1);
  const invalidate = useInvalidateDemandeAchats();

  const items = listQuery.data?.items ?? [];
  const totalCount = listQuery.data?.total ?? 0;
  const deletedItems = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? listQuery.isInitialLoading : deletedQuery.isInitialLoading;

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const toggleExpand = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Form dialog handlers
  const handleOpenAdd = () => {
    setEditingId(undefined);
    setFormMinimized(false); // clicking "Add" restores a minimized dialog
    setFormOpen(true);
  };

  const handleOpenEdit = (id: number) => {
    setEditingId(id);
    setFormMinimized(false); // clicking "Edit" restores a minimized dialog
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    invalidate();
  };

  // Delete / Restore
  const handleDelete = (da: DemandeAchat) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer la demande d'achat "${da.reference}" ?`,
      async () => {
        try {
          await demandesAchatAPI.delete(da.id);
          showSuccess('Demande d\'achat supprimée avec succès');
          invalidate();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la suppression', confirmText: 'Supprimer' },
    );
  };

  const handleRestore = (da: DemandeAchat) => {
    showConfirm(
      `Restaurer la demande d'achat "${da.reference}" ?`,
      async () => {
        try {
          await demandesAchatAPI.restore(da.id);
          showSuccess('Demande d\'achat restaurée avec succès');
          invalidate();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la restauration', confirmText: 'Restaurer' },
    );
  };

  // Status menu
  const openStatusMenu = (event: React.MouseEvent<HTMLElement>, da: DemandeAchat) => {
    setMenuAnchor(event.currentTarget);
    setMenuTargetDA(da);
  };

  const closeStatusMenu = () => {
    setMenuAnchor(null);
    setMenuTargetDA(null);
  };

  const triggerStatusChange = (newStatus: string, label: string) => {
    if (!menuTargetDA) return;
    setStatusDialogTarget({ id: menuTargetDA.id, reference: menuTargetDA.reference, newStatus, label });
    setStatusComment('');
    setStatusDialogOpen(true);
    closeStatusMenu();
  };

  const handleConfirmStatusChange = async () => {
    if (!statusDialogTarget) return;
    try {
      await demandesAchatAPI.updateStatus(statusDialogTarget.id, {
        status: statusDialogTarget.newStatus,
        commentaire_manager: statusComment.trim() || null,
      });
      showSuccess(`Statut mis à jour: ${statusDialogTarget.label}`);
      setStatusDialogOpen(false);
      setStatusDialogTarget(null);
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const renderActiveTable = () => (
    <>
      <TableContainer sx={getModuleTableTheme('stock')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }} />
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date
                  <ColumnFilter
                    type="dateRange"
                    value={filters.date}
                    onChange={(val) => update('date', val)}
                    label="Date"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Demandeur
                  {isManager && (
                    <ColumnFilter
                      type="multiAutocomplete"
                      value={filters.user_ids}
                      onChange={(val) => update('user_ids', val)}
                      options={users.map((u) => ({ value: u.id, label: u.full_name || u.email }))}
                      label="Demandeur"
                      color={STOCK_COLOR}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Projet
                  <ColumnFilter
                    type="multiAutocomplete"
                    value={filters.projet_ids}
                    onChange={(val) => update('projet_ids', val)}
                    options={projets.map((p) => ({ value: p.id, label: `${p.code} - ${p.nom}` }))}
                    label="Projet"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  N° DA
                  <ColumnFilter
                    type="text"
                    value={filters.reference}
                    onChange={(val) => update('reference', val)}
                    label="Référence"
                    placeholder="Ex: DA26-001"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Désignation
                  <ColumnFilter
                    type="text"
                    value={filters.designation}
                    onChange={(val) => update('designation', val)}
                    label="Désignation"
                    placeholder="Rechercher dans les articles..."
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Statut
                  <ColumnFilter
                    type="multiSelect"
                    value={filters.statuses}
                    onChange={(val) => update('statuses', val)}
                    options={STATUS_OPTIONS}
                    label="Statut"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ width: 180 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Aucune demande d'achat</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((da) => {
                const isExpanded = expandedRows.has(da.id);
                const canChangeStatus = isManager;
                const canEdit = isManager || da.status === DemandeAchatStatus.EN_COURS_TRAITEMENT;
                return (
                  <React.Fragment key={da.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(da.id)}>
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{formatDate(da.date)}</TableCell>
                      <TableCell>{da.user_name || '-'}</TableCell>
                      <TableCell>
                        {da.projet_code ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{da.projet_code}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {da.projet_nom}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600} sx={{ color: STOCK_COLOR }}>
                          {da.reference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`${da.total_items} article${da.total_items > 1 ? 's' : ''}`}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">{getStatusChip(da.status)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {canChangeStatus && (
                            <Tooltip title="Changer le statut">
                              <IconButton
                                size="small"
                                onClick={(e) => openStatusMenu(e, da)}
                                sx={{ color: STOCK_COLOR }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <PermissionButton
                            moduleName="Stock"
                            operation="edit"
                            miniModuleName="Demandes Achat"
                            component="IconButton"
                            disabled={!canEdit}
                            tooltipTitle={
                              !canEdit
                                ? 'Modification bloquée: la demande n\'est plus en cours de traitement'
                                : undefined
                            }
                            onClick={() => handleOpenEdit(da.id)}
                          >
                            <EditIcon fontSize="small" />
                          </PermissionButton>
                          <PermissionButton
                            moduleName="Stock"
                            operation="delete"
                            miniModuleName="Demandes Achat"
                            component="IconButton"
                            color="error"
                            onClick={() => handleDelete(da)}
                          >
                            <DeleteIcon fontSize="small" />
                          </PermissionButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            {/* Status timeline */}
                            <Typography variant="subtitle2" gutterBottom sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
                              Historique des statuts
                            </Typography>
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              {buildTimeline(da).map((entry, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
                                  <Box>
                                    <Typography variant="caption" fontWeight={600} display="block">
                                      {entry.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {formatDateTime(entry.at)}
                                      {entry.by ? ` — ${entry.by}` : ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>

                            {da.commentaire_manager && (
                              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight={600}>Commentaire manager:</Typography>
                                <Typography variant="body2">{da.commentaire_manager}</Typography>
                              </Box>
                            )}

                            <Typography variant="subtitle2" gutterBottom sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
                              Articles demandés
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Désignation</TableCell>
                                  <TableCell align="right">Quantité</TableCell>
                                  <TableCell>Unité</TableCell>
                                  <TableCell>Commentaire</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {da.items.map((it) => (
                                  <TableRow key={it.id}>
                                    <TableCell>
                                      {it.article_reference && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {it.article_reference}
                                        </Typography>
                                      )}
                                      <Typography variant="body2">{it.designation}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{Number(it.quantite).toLocaleString('fr-FR')}</TableCell>
                                    <TableCell>{it.unit || '-'}</TableCell>
                                    <TableCell>{it.commentaire || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );

  const renderDeletedTable = () => (
    <TableContainer sx={getModuleTableTheme('stock')}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Demandeur</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>N° DA</TableCell>
            <TableCell>Articles</TableCell>
            <TableCell align="center">Statut</TableCell>
            <TableCell align="center">Supprimé le</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : deletedItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">Aucune demande supprimée</Typography>
              </TableCell>
            </TableRow>
          ) : (
            deletedItems.map((da) => (
              <TableRow key={da.id} hover>
                <TableCell>{formatDate(da.date)}</TableCell>
                <TableCell>{da.user_name || '-'}</TableCell>
                <TableCell>
                  <Typography fontWeight={600} sx={{ color: STOCK_COLOR }}>
                    {da.reference}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={`${da.total_items} article${da.total_items > 1 ? 's' : ''}`} variant="outlined" />
                </TableCell>
                <TableCell align="center">{getStatusChip(da.status)}</TableCell>
                <TableCell align="center">{formatDate(da.deleted_at)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Restaurer">
                    <IconButton size="small" color="success" onClick={() => handleRestore(da)}>
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
            Demandes d'Achat
          </Typography>
          <PermissionButton
            moduleName="Stock"
            operation="add"
            miniModuleName="Demandes Achat"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ bgcolor: STOCK_COLOR, '&:hover': { bgcolor: '#1E88E5' } }}
          >
            Nouvelle demande d'achat
          </PermissionButton>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, v) => {
            setTabValue(v);
            setPage(0);
          }}
          sx={{ mb: 2 }}
        >
          <Tab label={`Actives (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>

        {tabValue === 0 && (
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Rechercher (référence ou désignation)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 320 }}
            />
            {hasActive() && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={reset}
                sx={{ color: STOCK_COLOR }}
              >
                Effacer filtres colonnes
              </Button>
            )}
          </Stack>
        )}

        {tabValue === 0 ? renderActiveTable() : renderDeletedTable()}
      </Paper>

      {/* Status change menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeStatusMenu}>
        <MenuItem onClick={() => triggerStatusChange(DemandeAchatStatus.VALIDE, 'Validé')}>
          <ValidateIcon sx={{ mr: 1, color: 'success.main' }} fontSize="small" /> Valider
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(DemandeAchatStatus.REFUSE, 'Refusé')}>
          <RefuseIcon sx={{ mr: 1, color: 'error.main' }} fontSize="small" /> Refuser
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(DemandeAchatStatus.BLOQUE, 'Bloqué')}>
          <BlockIcon sx={{ mr: 1, color: 'grey.700' }} fontSize="small" /> Bloquer
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(DemandeAchatStatus.EN_COURS_LIVRAISON, 'En cours de livraison')}>
          <DeliveryIcon sx={{ mr: 1, color: 'info.main' }} fontSize="small" /> En cours de livraison
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(DemandeAchatStatus.RECEPTIONNE, 'Réceptionné')}>
          <ReceivedIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" /> Réceptionné
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(DemandeAchatStatus.EN_COURS_TRAITEMENT, 'En cours de traitement')}>
          Remettre en traitement
        </MenuItem>
      </Menu>

      {/* Status confirmation dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Changer le statut: {statusDialogTarget?.label}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            Vous êtes sur le point de changer le statut de <strong>{statusDialogTarget?.reference}</strong> vers{' '}
            <strong>{statusDialogTarget?.label}</strong>.
          </Typography>
          <TextField
            autoFocus
            label="Commentaire (optionnel)"
            multiline
            rows={3}
            fullWidth
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Ajouter une note pour le demandeur..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleConfirmStatusChange}
            sx={{ bgcolor: STOCK_COLOR, '&:hover': { bgcolor: '#1E88E5' } }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <DemandeAchatFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setFormMinimized(false);
        }}
        onSuccess={handleFormSuccess}
        demandeAchatId={editingId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default DemandesAchatManagement;
